import { ReactNode } from 'react';
import {
  Box,
  Chip,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { Link } from '@backstage/core-components';
import {
  ResultHighlight,
  SearchDocument,
} from '@backstage/plugin-search-common';
import { searchPlugin } from '@backstage/plugin-search';
import {
  createSearchResultListItemExtension,
  HighlightedSearchResultText,
} from '@backstage/plugin-search-react';

// Renders `api-definition` search results, i.e. one OpenAPI
// operation or AsyncAPI channel, linking straight to it in the API definition tab.

type ApiDefinitionDocument = SearchDocument & {
  displayTitle?: string;
  apiTitle?: string;
  operation?: string;
  type?: string;
  owner?: string;
};

type ApiDefinitionSearchResultListItemProps = {
  icon?: ReactNode;
  result?: SearchDocument;
  highlight?: ResultHighlight;
  rank?: number;
};

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * The indexed title also lists the operation name split into words, so it ranks
 * partial-name matches; the card shows `displayTitle` instead. This re-applies the
 * words the search engine highlighted in the indexed title to `displayTitle`.
 */
export function highlightDisplayTitle(
  displayTitle: string,
  highlight?: ResultHighlight,
): string | undefined {
  const highlightedTitle = highlight?.fields.title;
  if (!highlight || !highlightedTitle) {
    return undefined;
  }
  const { preTag, postTag } = highlight;
  const words = new Set<string>();
  let start = highlightedTitle.indexOf(preTag);
  while (start !== -1) {
    const end = highlightedTitle.indexOf(postTag, start);
    if (end === -1) {
      break;
    }
    const word = highlightedTitle.slice(start + preTag.length, end).trim();
    if (word.length > 1) {
      words.add(word.toLocaleLowerCase('en-US'));
    }
    start = highlightedTitle.indexOf(preTag, end);
  }
  if (!words.size) {
    return undefined;
  }
  // Longest first, so `squish-vins` wins over `squish`.
  const pattern = new RegExp(
    Array.from(words)
      .sort((a, b) => b.length - a.length)
      .map(escapeRegExp)
      .join('|'),
    'gi',
  );
  return displayTitle.replace(pattern, match => `${preTag}${match}${postTag}`);
}

const useStyles = makeStyles({
  item: { display: 'flex' },
  flexContainer: { flexWrap: 'wrap' },
  itemText: { width: '100%', wordBreak: 'break-word', marginBottom: '1rem' },
});

const ApiDefinitionSearchResultListItemComponent = ({
  icon,
  result,
  highlight,
}: ApiDefinitionSearchResultListItemProps) => {
  const classes = useStyles();
  if (!result) {
    return null;
  }
  const document = result as ApiDefinitionDocument;
  // The indexed text starts with the operation summary, followed by its details.
  const summary = document.text.split(' : ')[0];
  const displayTitle = document.displayTitle ?? document.title;
  const highlightedTitle = highlightDisplayTitle(displayTitle, highlight);

  return (
    <div className={classes.item}>
      {icon && <ListItemIcon>{icon}</ListItemIcon>}
      <div className={classes.flexContainer}>
        <ListItemText
          className={classes.itemText}
          primaryTypographyProps={{ variant: 'h6' }}
          primary={
            <Link noTrack to={document.location}>
              {highlight && highlightedTitle ? (
                <HighlightedSearchResultText
                  text={highlightedTitle}
                  preTag={highlight.preTag}
                  postTag={highlight.postTag}
                />
              ) : (
                displayTitle
              )}
            </Link>
          }
          secondary={
            <>
              <Typography
                component="span"
                variant="body2"
                color="textSecondary"
                style={{ display: 'block' }}
              >
                {summary}
              </Typography>
              {highlight?.fields.text && (
                <Typography
                  component="span"
                  variant="body2"
                  color="textSecondary"
                  style={{
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 2,
                    overflow: 'hidden',
                  }}
                >
                  Matched in: …
                  <HighlightedSearchResultText
                    text={highlight.fields.text}
                    preTag={highlight.preTag}
                    postTag={highlight.postTag}
                  />
                  …
                </Typography>
              )}
            </>
          }
        />
        <Box>
          {document.apiTitle && (
            <Chip label={`API: ${document.apiTitle}`} size="small" />
          )}
          {document.type && (
            <Chip label={`Type: ${document.type}`} size="small" />
          )}
          {document.owner && (
            <Chip label={`Owner: ${document.owner}`} size="small" />
          )}
        </Box>
      </div>
    </div>
  );
};

export const ApiDefinitionSearchResultListItem = searchPlugin.provide(
  createSearchResultListItemExtension({
    name: 'ApiDefinitionSearchResultListItem',
    component: async () => ApiDefinitionSearchResultListItemComponent,
    predicate: result => result.type === 'api-definition',
  }),
);
